/**
 * Utility functions for workout session management
 */
import { WorkoutSession } from '../types/workout';
import { Segment } from '../types/segment';
import { Lap } from '../types/stopwatch';
import { saveWorkoutSession } from '../services/StorageService';
import { convertLapToWorkoutLap } from './lapUtils';

/**
 * Create a new workout session object
 */
export const createWorkoutSessionObject = (): WorkoutSession => {
  // Generate a new session ID
  const sessionId = `session-${Date.now()}`;

  // Create a new session object with all required fields
  return {
    id: sessionId,
    date: new Date(),
    totalTime: 0,
    laps: [],
    segments: {},
  };
};

/**
 * Update a workout session with new lap data
 */
export const addLapToSession = (
  session: WorkoutSession | null,
  lapData: Lap
): WorkoutSession | null => {
  if (!session) return null;

  // Convert Lap to WorkoutLap (pace will be preserved via the conversion)
  const workoutLap = convertLapToWorkoutLap(lapData);

  const updatedSession: WorkoutSession = {
    ...session,
    laps: [...session.laps, workoutLap],
    lapCount: session.lapCount ? session.lapCount + 1 : 1,
  };

  // Save the updated session
  saveWorkoutSession(updatedSession);

  return updatedSession;
};

/**
 * Update a workout session with new segment data
 */
export const addSegmentToSession = (
  session: WorkoutSession | null,
  segmentData: Segment
): WorkoutSession | null => {
  if (!session || !segmentData) return null;

  // Update segments property
  const updatedSegmentsMap = { ...session.segments };
  if (!updatedSegmentsMap[segmentData.lapNumber]) {
    updatedSegmentsMap[segmentData.lapNumber] = [];
  }
  updatedSegmentsMap[segmentData.lapNumber].push(segmentData);

  const updatedSession: WorkoutSession = {
    ...session,
    segments: updatedSegmentsMap,
  };

  // Save the updated session
  saveWorkoutSession(updatedSession);

  return updatedSession;
};

/**
 * Find a session by ID in a list of sessions
 */
export const findSessionById = (
  sessions: WorkoutSession[],
  sessionId: string | null
): WorkoutSession | null => {
  if (!sessionId) return null;
  return sessions.find(session => session.id === sessionId) || null;
};
